// This file contains legitimate console usage for error logging and filtering

interface ErrorWithDetails extends Error {
  details?: unknown;
}

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const errorObj = error as { message?: unknown; stack?: unknown };
    const message = typeof errorObj.message === 'string' ? errorObj.message : 'Unknown error';
    const normalized = new Error(message);
    if (errorObj.stack && typeof errorObj.stack === 'string') {
      normalized.stack = errorObj.stack;
    }
    (normalized as ErrorWithDetails).details = error;
    return normalized;
  }

  return new Error('Unknown error');
};

const generateTraceId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const sanitizeContext = (context: unknown): unknown => {
  if (!context) {
    return undefined;
  }

  if (typeof context === 'string') {
    return context;
  }

  if (typeof context !== 'object') {
    return { value: context };
  }

  try {
    return JSON.parse(
      JSON.stringify(context, (_key, value) => {
        if (typeof value === 'function') {
          return '[function]';
        }
        if (value instanceof Error) {
          return {
            message: value.message,
            stack: value.stack,
            name: value.name,
          };
        }

        return value;
      })
    ) as Record<string, unknown>;
  } catch {
    return { note: 'context_not_serializable' };
  }
};

// Helper function to get browser info
const getBrowserInfo = () => {
  const isBrowser = typeof window !== 'undefined';
  const url = isBrowser ? window.location.href : undefined;
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
  const language = typeof navigator !== 'undefined' ? navigator.language : undefined;
  const online = typeof navigator !== 'undefined' ? navigator.onLine : undefined;

  return { isBrowser, url, userAgent, language, online };
};

// Helper function to get connection info
const getConnectionInfo = ():
  | { effectiveType?: string; downlink?: number; rtt?: number }
  | undefined => {
  try {
    if (typeof navigator !== 'undefined') {
      const navConnection = (navigator as { connection?: unknown }).connection as
        | { effectiveType?: string; downlink?: number; rtt?: number }
        | undefined;
      if (navConnection) {
        return {
          ...(navConnection.effectiveType && { effectiveType: navConnection.effectiveType }),
          ...(navConnection.downlink && { downlink: navConnection.downlink }),
          ...(navConnection.rtt && { rtt: navConnection.rtt }),
        };
      }
    }
  } catch {
    // Ignore connection access errors on some mobile browsers
  }
  return undefined;
};

const buildMetadata = (source: unknown, severity: unknown, context: unknown) => {
  const timestamp = new Date().toISOString();
  const { url, userAgent, language, online } = getBrowserInfo();
  const connection = getConnectionInfo();

  return {
    traceId: generateTraceId(),
    source,
    severity,
    timestamp,
    url,
    userAgent,
    language,
    online,
    connection,
    context: sanitizeContext(context),
  };
};

interface LogPayload {
  traceId: string;
  source: unknown;
  severity: unknown;
  timestamp: string;
  url?: string | undefined;
  userAgent?: string | undefined;
  language?: string | undefined;
  online?: boolean | undefined;
  connection?: { effectiveType?: string; downlink?: number; rtt?: number } | undefined;
  context: unknown;
  name: string;
  message: string;
  stack?: string | undefined;
  stacktrace?: string | undefined;
}

// Helper functions for SES error detection
const safeStringify = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '[object]'; // Avoid calling toString() on objects
};

const extractErrorStrings = (error: unknown, context: unknown) => {
  const errorObj = error as { message?: unknown; stack?: unknown };
  const contextObj = context as { filename?: unknown };

  const rawErrorMessage = safeStringify(errorObj.message ?? error);
  const errorStack = safeStringify(errorObj.stack);
  const contextFilename = safeStringify(contextObj.filename);

  return {
    rawErrorMessage,
    errorMessage: rawErrorMessage.toLowerCase(),
    errorStack: errorStack.toLowerCase(),
    contextFilename: contextFilename.toLowerCase(),
  };
};

const hasSesIndicators = (
  rawErrorMessage: string,
  errorMessage: string,
  errorStack: string,
  contextFilename: string
): boolean => {
  return (
    /ses_uncaught_exception/i.test(rawErrorMessage) ||
    errorMessage.includes('ses_uncaught_exception') ||
    errorStack.includes('lockdown-install.js') ||
    contextFilename.includes('lockdown-install.js')
  );
};

const isSesError = (error: unknown, context: unknown): boolean => {
  const { rawErrorMessage, errorMessage, errorStack, contextFilename } = extractErrorStrings(
    error,
    context
  );
  return hasSesIndicators(rawErrorMessage, errorMessage, errorStack, contextFilename);
};

export const logError = (
  error: unknown,
  options: { source?: string; context?: unknown; severity?: string } = {}
): LogPayload | null => {
  const { source = 'application', context = null, severity = 'error' } = options;

  // Early exit for SES-related errors from browser extensions
  if (isSesError(error, context)) {
    // Silently ignore SES errors from browser extensions
    return null;
  }

  const normalizedError = normalizeError(error);
  const metadata = buildMetadata(source, severity, context);
  const payload = {
    ...metadata,
    name: normalizedError.name,
    message: normalizedError.message,
    stack: normalizedError.stack,
    stacktrace: normalizedError.stack,
  };

  const consoleFn = severity === 'warn' ? console.warn : console.error;
  if (typeof consoleFn === 'function') {
    consoleFn(`[${metadata.timestamp}] [${source}] (trace:${metadata.traceId})`, payload);
  }

  // Only attempt network logging if we have a valid endpoint and are in a browser environment
  const endpoint = import.meta.env.VITE_ERROR_LOG_ENDPOINT as string | undefined;
  if (endpoint && typeof window !== 'undefined') {
    try {
      if (typeof window.navigator.sendBeacon === 'function') {
        const blob = new Blob([JSON.stringify(payload)], {
          type: 'application/json',
        });
        // sendBeacon is fire-and-forget, so we don't need to handle its result
        window.navigator.sendBeacon(endpoint, blob);
      } else if (typeof fetch === 'function') {
        // Use fire-and-forget fetch with keepalive
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {
          // Silently ignore fetch errors to prevent error loops
        });
      }
    } catch {
      // Silently ignore any errors in the network logging process to prevent loops
    }
  }

  return payload;
};

let globalHandlersRegistered = false;
let consoleInterceptionInitialized = false;

export const initConsoleInterception = (): void => {
  if (consoleInterceptionInitialized) {
    return;
  }
  consoleInterceptionInitialized = true;

  // Intercept console.error to filter out benign external errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const rawMessage = String(args[0] ?? '');
    const message = rawMessage.toLowerCase();

    // Check if the second argument is an object with SES-related content
    const secondArg = args[1] as Record<string, unknown> | undefined;
    const hasSesinPayload =
      secondArg &&
      typeof secondArg === 'object' &&
      secondArg !== null &&
      ((typeof (secondArg as Record<string, unknown>).message === 'string'
        ? String((secondArg as Record<string, unknown>).message)
        : ''
      ).match(/ses_uncaught_exception/i) ??
        (typeof (secondArg as Record<string, unknown>).name === 'string'
          ? String((secondArg as Record<string, unknown>).name)
          : ''
        ).match(/ses_uncaught_exception/i));

    // Filter out benign errors from browser extensions and development tools
    if (
      /ses_uncaught_exception/i.test(rawMessage) ||
      message.includes('ses_uncaught_exception') ||
      message.includes('lockdown-install.js') ||
      hasSesinPayload ||
      (message.includes('websocket connection') && message.includes('failed')) ||
      message.includes('download the react devtools') ||
      message.includes('react devtools') ||
      message.includes('better development experience') ||
      rawMessage ===
        'Download the React DevTools for a better development experience: https://reactjs.org/link/react-devtools' ||
      rawMessage.includes('chunk-lpf6ksf2.js') || // Vite dev chunks
      (rawMessage.includes('chunk-') && rawMessage.includes('react devtools')) // Vite chunks with React DevTools
    ) {
      // Silently ignore these errors
      return;
    }

    // Otherwise, call the original console.error
    originalConsoleError.apply(console, args);
  };

  // Intercept console.warn to filter out React DevTools warning
  const originalConsoleWarn = console.warn;
  console.warn = (...args) => {
    const rawMessage = String(args[0] ?? '');
    const message = rawMessage.toLowerCase();

    // Filter out React DevTools warning
    if (
      message.includes('download the react devtools') ||
      message.includes('react devtools') ||
      message.includes('better development experience') ||
      rawMessage.includes('chunk-lpf6ksf2.js') || // Vite dev chunks
      (rawMessage.includes('chunk-') && rawMessage.includes('react devtools')) // Vite chunks with React DevTools
    ) {
      // Silently ignore this warning
      return;
    }

    // Otherwise, call the original console.warn
    originalConsoleWarn.apply(console, args);
  };

  // Intercept console.log to filter out React DevTools message
  const originalConsoleLog = console.log;
  console.log = (...args) => {
    const rawMessage = String(args[0] ?? '');
    const message = rawMessage.toLowerCase();

    // Filter out React DevTools message
    if (
      message.includes('download the react devtools') ||
      message.includes('react devtools') ||
      message.includes('better development experience') ||
      rawMessage ===
        'Download the React DevTools for a better development experience: https://reactjs.org/link/react-devtools' ||
      rawMessage.includes('chunk-lpf6ksf2.js') || // Vite dev chunks
      (rawMessage.includes('chunk-') && rawMessage.includes('react devtools')) || // Vite chunks with React DevTools
      rawMessage.includes('Initializing Tag:') // GTM initialization
    ) {
      // Silently ignore this message
      return;
    }

    // Otherwise, call the original console.log
    originalConsoleLog.apply(console, args);
  };
};

const formatReason = (reason: unknown): Error => {
  if (reason instanceof Error) {
    return reason;
  }
  if (typeof reason === 'string') {
    return new Error(reason);
  }
  return new Error('Unhandled promise rejection');
};

// Certain browser extensions (e.g. security / wallet extensions) inject
// scripts like "lockdown-install.js" that use SES and can throw
// `SES_UNCAUGHT_EXCEPTION` errors. These are outside of our app and can
// generate a lot of noisy, unactionable global error events.
// Also filters out benign development-only errors like WebSocket connection failures.
const isBenignExternalError = (event: unknown): boolean => {
  try {
    // Early return if event is not an object
    if (!event || typeof event !== 'object') {
      return false;
    }

    // Get the raw message first to check for exact patterns
    const eventObj = event as {
      message?: unknown;
      filename?: unknown;
      error?: { message?: unknown; stack?: unknown; name?: unknown };
    };
    const rawMessage = typeof eventObj.message === 'string' ? eventObj.message : '';
    const filename = typeof eventObj.filename === 'string' ? eventObj.filename : '';
    const message = rawMessage.toLowerCase();
    const errorMsg = typeof eventObj.error?.message === 'string' ? eventObj.error.message : '';
    const errorStack = typeof eventObj.error?.stack === 'string' ? eventObj.error.stack : '';
    const errorName = typeof eventObj.error?.name === 'string' ? eventObj.error.name : '';

    // Special case: Check for exact SES_UNCAUGHT_EXCEPTION pattern (case-insensitive)
    if (/ses_uncaught_exception/i.test(rawMessage)) {
      return true;
    }

    // Special case: if error is null and message contains SES, it's benign
    if (!eventObj.error && message.includes('ses_uncaught_exception')) {
      return true;
    }

    // Combine all message sources for checking
    const allMessages = [message, errorMsg, errorName].join(' ');
    const allSources = [filename, errorStack].join(' ');

    // Ignore SES lockdown errors from injected scripts
    // Check filename, stack trace, and error messages
    if (
      allSources.includes('lockdown-install.js') ||
      allSources.includes('lockdown-install') ||
      filename.includes('lockdown-install.js')
    ) {
      return true;
    }

    // Check for SES-related error messages (case-insensitive)
    if (
      allMessages.includes('ses_uncaught_exception') ||
      allMessages.includes('ses_uncaught_exception:') ||
      message.includes('ses_uncaught_exception')
    ) {
      return true;
    }

    // Ignore benign WebSocket connection errors (common in development)
    // These are typically non-critical for the app functionality
    if (allSources.includes('websocket-factory') || allSources.includes('realtimeclient')) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

export const initGlobalErrorHandling = (): void => {
  if (globalHandlersRegistered || typeof window === 'undefined') {
    return;
  }

  // Initialize console interception immediately to catch early messages
  try {
    initConsoleInterception();
  } catch (error) {
    // Silently fail if console interception fails
    console.warn('Failed to initialize console interception:', error);
  }

  const extractEventProperties = (event: unknown) => {
    let rawEventMessage = '';
    let eventFilename = '';
    let errorStack = '';
    let errorMsg = '';

    try {
      const eventObj = event as {
        message?: unknown;
        filename?: unknown;
        error?: { stack?: unknown; message?: unknown };
      };
      rawEventMessage = typeof eventObj.message === 'string' ? eventObj.message : '';
      eventFilename = typeof eventObj.filename === 'string' ? eventObj.filename : '';
      errorStack = typeof eventObj.error?.stack === 'string' ? eventObj.error.stack : '';
      errorMsg = typeof eventObj.error?.message === 'string' ? eventObj.error.message : '';
    } catch {
      // If accessing event properties fails on mobile, use fallback
      rawEventMessage = 'Error accessing event properties';
      eventFilename = '';
      errorStack = '';
      errorMsg = '';
    }

    return { rawEventMessage, eventFilename, errorStack, errorMessage: errorMsg };
  };

  const _createCombinedErrorInfo = (
    rawEventMessage: string,
    eventFilename: string,
    errorStack: string,
    errorMessage: string
  ): string => {
    try {
      return [rawEventMessage, eventFilename, errorStack, errorMessage].join(' ').toLowerCase();
    } catch {
      return 'error combining event info';
    }
  };

  const isReactDevToolsMessage = (message: string): boolean => {
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('download the react devtools') ||
      lowerMessage.includes('react devtools') ||
      lowerMessage.includes('better development experience') ||
      lowerMessage.includes('chunk-lpf6ksf2.js') ||
      (lowerMessage.includes('chunk-') && lowerMessage.includes('react devtools')) ||
      lowerMessage ===
        'download the react devtools for a better development experience: https://reactjs.org/link/react-devtools'
    );
  };

  const preventEventPropagation = (event: unknown): void => {
    const eventObj = event as {
      preventDefault?: () => void;
      stopPropagation?: () => void;
      stopImmediatePropagation?: () => void;
    };
    if (typeof eventObj.preventDefault === 'function') {
      eventObj.preventDefault();
    }
    if (typeof eventObj.stopPropagation === 'function') {
      eventObj.stopPropagation();
    }
    if (typeof eventObj.stopImmediatePropagation === 'function') {
      eventObj.stopImmediatePropagation();
    }
  };

  const handleWindowError = (event: unknown): void => {
    const { rawEventMessage, eventFilename, errorStack, errorMessage } =
      extractEventProperties(event);

    // Check for React DevTools messages first - BEFORE any logging
    if (isReactDevToolsMessage(rawEventMessage)) {
      // Silently ignore React DevTools messages - prevent default and return early
      preventEventPropagation(event);
      return;
    }

    // Check for SES errors and other benign external errors
    if (isBenignExternalError(event)) {
      // Prevent the error from bubbling
      preventEventPropagation(event);
      return;
    }

    // Additional filtering for React DevTools and development noise
    const combinedMessage = [rawEventMessage, eventFilename, errorStack, errorMessage]
      .join(' ')
      .toLowerCase();
    if (
      combinedMessage.includes('download the react devtools') ||
      combinedMessage.includes('react devtools') ||
      combinedMessage.includes('better development experience') ||
      combinedMessage.includes('chunk-lpf6ksf2.js') || // Vite dev chunk
      (combinedMessage.includes('chunk-') && combinedMessage.includes('react devtools')) || // Vite chunks with React DevTools
      combinedMessage.includes('initializing tag: gtm-') || // GTM initialization
      combinedMessage.includes('gtm-n9wg7pt4') || // Specific GTM tag
      combinedMessage.includes('initializing tag:') // GTM initialization (case insensitive)
    ) {
      preventEventPropagation(event);
      return;
    }

    // In development, be more selective about what errors to log
    // Only log errors that seem critical (not development-related noise)
    const eventObj = event as {
      error?: unknown;
      message?: unknown;
      filename?: unknown;
      lineno?: unknown;
      colno?: unknown;
    };

    const errorMsg = (typeof eventObj.message === 'string' ? eventObj.message : '').toLowerCase();
    const errorObj = eventObj.error as { name?: unknown } | undefined;
    const errorName = (typeof errorObj?.name === 'string' ? errorObj.name : '').toLowerCase();

    // Filter out common development-time errors that are not actionable
    const isDevelopmentNoise =
      errorMsg.includes('loading chunk') ||
      errorMsg.includes('chunk load error') ||
      (errorMsg.includes('network error') && errorMsg.includes('development')) ||
      errorName.includes('chunkerror') ||
      (errorMsg.includes('failed to fetch') && errorMsg.includes('localhost')) ||
      errorMsg.includes('vite') ||
      errorMsg.includes('hot update') ||
      errorMsg.includes('webpack') ||
      errorMsg.includes('development server') ||
      errorMsg.includes('localhost:') ||
      errorMsg.includes('127.0.0.1:');

    // Filter out image loading errors that are typically not actionable
    const isImageLoadingError =
      errorMsg.includes('failed to load resource') ||
      errorMsg.includes('img src') ||
      errorMsg.includes('image load') ||
      errorMsg.includes('404') ||
      errorMsg.includes('403') ||
      errorMsg.includes('net::err_failed') ||
      errorMsg.includes('net::err_aborted') ||
      errorMsg.includes('net::err_connection_refused') ||
      (errorMsg.includes('get') &&
        (errorMsg.includes('.jpg') ||
          errorMsg.includes('.png') ||
          errorMsg.includes('.webp') ||
          errorMsg.includes('.jpeg') ||
          errorMsg.includes('.svg') ||
          errorMsg.includes('.ico'))) ||
      (eventFilename &&
        (eventFilename.includes('.jpg') ||
          eventFilename.includes('.png') ||
          eventFilename.includes('.webp') ||
          eventFilename.includes('.jpeg') ||
          eventFilename.includes('.svg') ||
          eventFilename.includes('.ico')));

    // Filter out additional development and network errors
    const isNetworkOrScriptError =
      errorMsg.includes('script error') ||
      errorMsg.includes('network error') ||
      errorMsg.includes('connection error') ||
      errorMsg.includes('timeout') ||
      errorMsg.includes('cors') ||
      errorMsg.includes('cross-origin') ||
      errorMsg.includes('mixed content') ||
      errorMsg.includes('certificate') ||
      errorMsg.includes('ssl') ||
      errorMsg.includes('tls') ||
      errorName.includes('networkerror') ||
      errorName.includes('timeouterror') ||
      errorName.includes('aborterror');

    if (isDevelopmentNoise || isImageLoadingError || isNetworkOrScriptError) {
      // Silently ignore development noise, image loading errors, and network errors
      preventEventPropagation(event);
      return;
    }

    // Rate limit error logging to prevent spam - only log once per second per error type
    const now = Date.now();
    const errorKey = `${String(eventObj.message ?? 'unknown')}-${String(eventObj.filename ?? 'unknown')}-${String(eventObj.lineno ?? 'unknown')}`;
    const lastLogTime = (
      window as {
        _errorLogTimes?: Record<string, number>;
      }
    )._errorLogTimes?.[errorKey];

    if (lastLogTime && now - lastLogTime < 1000) {
      // Skip logging this error - too soon after the last one
      preventEventPropagation(event);
      return;
    }

    // Update the last log time
    if (!(window as any)._errorLogTimes) {
      (window as any)._errorLogTimes = {};
    }
    (window as any)._errorLogTimes[errorKey] = now;

    // Log the error if it passes all filters
    try {
      const errorObj = eventObj.error as Error | undefined;
      console.log('DEBUG: Window error details:', {
        message: eventObj.message,
        filename: eventObj.filename,
        lineno: eventObj.lineno,
        colno: eventObj.colno,
        error: errorObj,
        stack: errorObj?.stack,
        fullEvent: eventObj,
      });
      logError(errorObj ?? eventObj.message ?? 'Window error', {
        source: 'global:window-error',
        context: {
          filename: eventObj.filename,
          lineno: eventObj.lineno,
          colno: eventObj.colno,
        },
      });
    } catch {
      // If logging fails, silently ignore to prevent recursive errors and loops
    }
  };

  const handleUnhandledRejection = (event: unknown): void => {
    const eventObj = event as { reason?: unknown };
    logError(formatReason(eventObj.reason), {
      source: 'global:unhandled-rejection',
      context: {
        reason:
          typeof eventObj.reason === 'string'
            ? eventObj.reason
            : (eventObj.reason as { message?: unknown }).message,
      },
    });
  };

  // Use capture phase to intercept errors before they bubble
  window.addEventListener('error', handleWindowError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  globalHandlersRegistered = true;
};
